import {
  Breadcrumb,
  Button,
  Card,
  Label,
  TextInput,
  Avatar,
} from "flowbite-react";
import type { FC } from "react";
import { useState, useEffect } from "react";
import { HiHome, HiCog, HiPhone } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { useNavigate } from "react-router";
import { User } from "../../services/user";
import toast from "react-hot-toast";

const UserSettingsPage: FC = function () {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (userData && userData.id) {
      setUser(userData);
    }
  }, []);

  if (!user) {
    return (
      <NavbarSidebarLayout>
        <div className="px-4 pt-6 pb-8">
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">Loading user data...</p>
          </div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-8">
        <div className="mb-6">
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item onClick={() => navigate("/")}>
              <div className="flex items-center gap-x-3 cursor-pointer">
                <HiHome className="text-xl" />
                <span className="dark:text-white">Home</span>
              </div>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Settings</Breadcrumb.Item>
          </Breadcrumb>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <HiCog className="text-purple-600 dark:text-purple-400" />
                Settings
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Manage your account settings and preferences
              </p>
            </div>
          </div>
        </div>

        {/* Profile Summary Card */}
        <Card className="mb-6 border-0 shadow-lg">
          <div className="flex items-center gap-6">
            <Avatar
              img={`https://ui-avatars.com/api/?name=${user.firstName || ''}+${user.lastName || ''}&background=random&color=fff`}
              alt={`${user.firstName || ''} ${user.lastName || ''}`}
              rounded
              size="xl"
            />
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {user.firstName || ""} {user.lastName || ""}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">{user.email}</p>
              {user.mobileNumber && (
                <p className="text-sm text-gray-500 dark:text-gray-500 flex items-center gap-2">
                  <HiPhone className="w-4 h-4" />
                  {user.mobileNumber}
                </p>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ProfileInformationCard user={user} setUser={setUser} loading={loading} setLoading={setLoading} />
          <PasswordInformationCard user={user} loading={loading} setLoading={setLoading} />
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

const ProfileInformationCard: FC<{ user: any; setUser: any; loading: boolean; setLoading: any }> = ({ user, setUser, loading, setLoading }) => {
  const [formData, setFormData] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    mobileNumber: user.mobileNumber || "",
    address: user.address || "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userService = new User("/users");
      const response = await userService.updateUser(user.id.toString(), formData);
      const updatedUser = response.data;
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success("Profile updated successfully!");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
        Profile Information
      </h3>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <TextInput
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <TextInput
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="email">Email Address</Label>
            <TextInput
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="mobileNumber">Mobile Number</Label>
            <TextInput
              id="mobileNumber"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              placeholder="Enter mobile number"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="address">Address</Label>
            <TextInput
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter address"
              className="mt-1"
            />
          </div>
          <div>
            <Button type="submit" color="primary" disabled={loading} className="w-full">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

const PasswordInformationCard: FC<{ user: any; loading: boolean; setLoading: any }> = ({ user, loading, setLoading }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      // Note: You'll need to implement a password change endpoint
      // For now, this is a placeholder
      toast.error("Password change functionality needs to be implemented on the backend");
      // const userService = new User("/users");
      // await userService.updatePassword(user.id.toString(), {
      //   currentPassword: formData.currentPassword,
      //   newPassword: formData.newPassword,
      // });
      // toast.success("Password updated successfully!");
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
        Change Password
      </h3>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <Label htmlFor="currentPassword">Current Password</Label>
            <TextInput
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Enter current password"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="newPassword">New Password</Label>
            <TextInput
              id="newPassword"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <TextInput
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              className="mt-1"
              required
            />
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Password requirements:
            </p>
            <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400 list-disc list-inside">
              <li>At least 8 characters long</li>
              <li>Include uppercase and lowercase letters</li>
              <li>Include at least one number</li>
            </ul>
          </div>
          <div>
            <Button type="submit" color="primary" disabled={loading} className="w-full">
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default UserSettingsPage;
